
import React, { useState } from 'react';
import { useTeacherAvailability } from '@/hooks/useTeacherAvailability';
import { DAY_NAMES, TeacherAvailabilityTemplate } from '@/types/teacherAvailability';
import { DayScheduleCard } from './DayScheduleCard';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';

interface WeeklyScheduleManagerProps {
  teacherCode: string;
}

interface TimeSlotFormData {
  startTime: string;
  endTime: string;
}

interface DeleteDialogState {
  isOpen: boolean;
  type: 'single' | 'all';
  slotId?: string;
  dayOfWeek?: number;
  dayName?: string;
  timeSlot?: string;
}

export function WeeklyScheduleManager({ teacherCode }: WeeklyScheduleManagerProps) {
  const { weeklySchedule, isLoading, addTimeSlot, updateTimeSlot, deleteTimeSlot, deleteAllSlotsForDay } = useTeacherAvailability(teacherCode);
  const [editingSlot, setEditingSlot] = useState<{ dayOfWeek: number; slotId?: string } | null>(null);
  const [tempSchedule, setTempSchedule] = useState<TimeSlotFormData>({
    startTime: '08:00',
    endTime: '17:00'
  });
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    isOpen: false,
    type: 'single'
  });

  const handleAddSlot = (dayOfWeek: number) => {
    setTempSchedule({
      startTime: '08:00',
      endTime: '17:00'
    });
    setEditingSlot({ dayOfWeek });
  };

  const handleEditSlot = (dayOfWeek: number, slot: TeacherAvailabilityTemplate) => {
    setTempSchedule({
      startTime: slot.start_time,
      endTime: slot.end_time
    });
    setEditingSlot({ dayOfWeek, slotId: slot.id });
  };

  const handleSave = async () => {
    if (!editingSlot) return;

    let success = false;
    if (editingSlot.slotId) {
      success = await updateTimeSlot(editingSlot.slotId, tempSchedule);
    } else {
      success = await addTimeSlot(editingSlot.dayOfWeek, tempSchedule);
    }

    if (success) {
      setEditingSlot(null);
    }
  };

  const handleCancel = () => {
    setEditingSlot(null);
  };

  const handleDeleteSlotClick = (slotId: string, dayOfWeek: number, slot: TeacherAvailabilityTemplate) => {
    const dayName = DAY_NAMES[dayOfWeek];
    const timeSlot = `${slot.start_time} - ${slot.end_time}`;
    
    setDeleteDialog({
      isOpen: true,
      type: 'single',
      slotId,
      dayOfWeek,
      dayName,
      timeSlot
    });
  };

  const handleDeleteAllSlotsClick = (dayOfWeek: number) => {
    const dayName = DAY_NAMES[dayOfWeek];
    
    setDeleteDialog({
      isOpen: true,
      type: 'all',
      dayOfWeek,
      dayName
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.type === 'single' && deleteDialog.slotId) {
      await deleteTimeSlot(deleteDialog.slotId);
    } else if (deleteDialog.type === 'all' && deleteDialog.dayOfWeek !== undefined) {
      await deleteAllSlotsForDay(deleteDialog.dayOfWeek);
    }
    
    setDeleteDialog({ isOpen: false, type: 'single' });
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ isOpen: false, type: 'single' });
  };

  if (isLoading) {
    return <div className="text-center py-4">กำลังโหลด...</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">ช่วงเวลารับงานสอน (พื้นฐาน)</h3>
      
      {Object.entries(DAY_NAMES)
        .sort(([a], [b]) => {
          const da = parseInt(a);
          const db = parseInt(b);
          // Sunday (0) sorts to the end so order is Mon → Sat → Sun
          const sa = da === 0 ? 7 : da;
          const sb = db === 0 ? 7 : db;
          return sa - sb;
        })
        .map(([day, dayName]) => {
        const dayOfWeek = parseInt(day);
        const slots = weeklySchedule[dayOfWeek] || [];
        const isEditingThisDay = editingSlot?.dayOfWeek === dayOfWeek;
        
        return (
          <DayScheduleCard
            key={day}
            dayOfWeek={dayOfWeek}
            dayName={dayName}
            slots={slots}
            isEditing={isEditingThisDay}
            tempSchedule={tempSchedule}
            setTempSchedule={setTempSchedule}
            onAddSlot={handleAddSlot}
            onEditSlot={handleEditSlot}
            onDeleteSlot={handleDeleteSlotClick}
            onDeleteAllSlots={handleDeleteAllSlotsClick}
            onSave={handleSave}
            onCancel={handleCancel}
            editingSlot={editingSlot}
          />
        );
      })}

      <DeleteConfirmDialog
        deleteDialog={deleteDialog}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}
